<template>
  <b-card id="page-preorders" no-body class="p-2">
    <b-card-header
      class="pl-0 pr-0 d-flex justify-content-between align-items-center"
    >
      <b-card-title>
        <h2>{{ $t(label("title")) }}</h2>
      </b-card-title>

      <b-button id="tooltip-create" variant="primary" @click="loadPreOrders()">
        <feather-icon icon="RefreshCcwIcon" class="mr-25" />
        <span class="text-nowrap">{{ $t(label("refresh")) }}</span>
      </b-button>
    </b-card-header>
    <Filterss
      :fields="fildsToFilters"
      :model="model"
      @filter-applied="applyFilters"
      @filter-reset="resetFilters"
    />

    <b-table
      id="preorders-table"
      responsive
      :head-variant="headVariant"
      :striped="striped"
      :bordered="bordered"
      :borderless="borderless"
      :outlined="outlined"
      :per-page="perPage"
      :busy="loading"
      :items="items"
      :fields="fields"
      show-empty
    >
      <template #head()="data">
        <span class="text-black">{{
          $t(labelHeader(data.label.toLowerCase()))
        }}</span>
      </template>

      <template #cell(date)="data">
        {{ dateFormat(data.item.date) }}
      </template>

      <template #cell(nameStatus)="data">
        <div class="d-flex align-items-center">
          <b-badge
            pill
            :variant="colorStatus(data.item.nameStatus)"
            class="badge-glow"
          >
            <span>{{ data.item.nameStatus }}</span>
          </b-badge>
        </div>
      </template>

      <template #cell(characteristics)="data">
        <span>{{ data.item.characteristics || "-" }}</span>
      </template>

      <template #cell(employees)="data">
        <div
          v-if="employeeDetails(data.item.employees).length"
          class="employee-assignment-list"
        >
          <div
            v-for="employee in employeeDetails(data.item.employees)"
            :key="employee.id"
            class="employee-assignment"
          >
            <span class="employee-assignment-name">{{ employee.name }}</span>
            <small v-if="employee.roles.length" class="text-muted">
              {{ employee.roles.join(", ") }}
            </small>
          </div>
        </div>
        <span v-else>-</span>
      </template>

      <template #cell(actions)="data">
        <button
          v-if="canAcceptPreOrder(data.item)"
          type="button"
          class="accept-preorder-button text-success mr-50"
          :disabled="acceptingPreOrderId === data.item.idOrder"
          :aria-label="$t(label('acceptPreOrder'))"
          :title="$t(label('acceptPreOrder'))"
          @click="onAcceptPreOrder(data.item)"
        >
          <feather-icon size="18" icon="CheckCircleIcon" />
        </button>
        <feather-icon
          v-if="canEdit"
          class="text-primary mr-50 cursor-pointer"
          size="18"
          icon="EditIcon"
          @click="onEdit(data.item.idOrder)"
        />
        <feather-icon
          v-if="data.item.urlChatBoot"
          class="text-success mr-50 cursor-pointer"
          size="18"
          icon="MessageCircleIcon"
          @click="onWhatsApp(data.item)"
        />
        <button
          type="button"
          class="sofia-action-button mr-50"
          aria-label="SOFIA"
          title="SOFIA"
          @click="onSOFIA(data.item)"
        >
          <img src="/log-sof-favicon.png" alt="" width="18" height="18" />
        </button>
        <feather-icon
          v-if="canDelete && !isEmployee"
          class="text-danger cursor-pointer"
          size="18"
          icon="Trash2Icon"
          @click="onDelete(data.item.idOrder)"
        />
      </template>

      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle ml-50"></b-spinner>
          <strong>{{ $t("loading") }}...</strong>
        </div>
      </template>

      <template #empty>
        <div class="text-center text-muted my-2">{{ $t("notRegister") }}</div>
      </template>
    </b-table>

    <div class="mx-2 mt-1">
      <b-row>
        <b-col cols="12" sm="12" md="2" lg="2" xl="2" class="mb-2">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{
              $t("perPage")
            }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="pageOptions"
              @change="handlePerPageChange"
              class="w-50"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="12"
          md="10"
          lg="10"
          xl="10"
          class="d-flex align-items-center justify-content-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            @change="handlePageChange"
            first-number
            last-number
          >
            <template #prev-text>
              <feather-icon size="18" icon="ChevronLeftIcon" />
            </template>
            <template #next-text>
              <feather-icon size="18" icon="ChevronRightIcon" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BTable,
  BPagination,
  BSpinner,
  BBadge,
  BFormSelect,
  BFormGroup,
  BButton,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import _ from "lodash";
import {
  allPreOrdersByCompany,
  getPreOrder,
  preOrderDelete,
  preOrderAccepted,
} from "@core/api/request/preOrders";
import { getCompany } from "@core/api/request/company";
import { createSignalRConnection } from "@core/api/request/signalr";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { canAccess } from "@/security/permissions/permissionHelper";
import Filterss from "../components/filters/Filterss.vue";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BTable,
    BPagination,
    BSpinner,
    BBadge,
    BFormSelect,
    BFormGroup,
    BButton,
    Filterss,
  },
  directives: {
    Ripple,
  },

  data: () => ({
    connection: null,
    socketRetryTimer: null,
    preOrdersRefreshTimer: null,
    preOrdersRequestId: 0,
    acceptingPreOrderId: null,
    socketGeneration: 0,
    isSocketDestroyed: false,
    loading: false,
    totalRows: 0,
    currentPage: 1,
    perPage: 10,
    pageOptions: [10, 50, 100],
    items: [],
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: false,
    companyGuid: null,
    whatsappPhone: null, // Número de teléfono para WhatsApp
    fields: [
      { label: "nro_order", key: "idOrder", sortable: true },
      { label: "client", key: "nameClient", sortable: true },
      { label: "employee", key: "employees", sortable: false },
      { label: "notes", key: "notes", sortable: false },
      { label: "date", key: "date", sortable: false },
      { label: "active", key: "nameStatus", sortable: false },
      { label: "action", key: "actions", sortable: false },
    ],
    hasNextPage: true,
    model: "preorders",

    fildsToFilters: [
      {
        name: "name",
        title: "name",
        placeholder: "name",
        type: "search",
        visible: true,
      },
    ],
    appliedFilters: {},
  }),
  computed: {
    canCreate() {
      return canAccess("Pre-órdenes", "creation");
    },
    canShow() {
      return canAccess("Pre-órdenes", "reading");
    },
    canEdit() {
      return canAccess("Pre-órdenes", "edition");
    },
    canDelete() {
      return canAccess("Pre-órdenes", "elimination");
    },
    divisa() {
      return this.$store.state.appConfig.divisa || "USD";
    },
    isEmployee() {
      return this.$store.getters["appConfig/isEmployee"];
    },
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.handleCompanyChange(guid);
    },
  },
  created() {
    this.companyGuid = this.$store.state.appConfig.activeCompanyGuid;
    if (this.companyGuid) {
      this.loadPreOrders();
      this.getCompanyGuid(this.companyGuid);
      this.setupPreOrdersSocket();
    }
  },
  methods: {
    employeeDetails(employees) {
      if (!Array.isArray(employees)) return [];

      return employees
        .map((assignment, index) => {
          const employee = assignment?.employee || {};
          const name = String(
            employee.name || assignment?.nameEmployee || "",
          ).trim();
          const roles = Array.isArray(employee.roles)
            ? employee.roles
                .map((role) => String(role?.nameRol || "").trim())
                .filter(Boolean)
            : [];

          return {
            id:
              assignment?.idOrderEmployee ||
              employee.idEmployee ||
              assignment?.idEmployee ||
              index,
            name,
            roles: [...new Set(roles)],
          };
        })
        .filter((employee) => employee.name);
    },

    async handleCompanyChange(guid) {
      const generation = ++this.socketGeneration;
      this.companyGuid = guid;
      this.items = [];
      this.totalRows = 0;
      this.hasNextPage = true;
      this.currentPage = 1;
      this.whatsappPhone = null;
      this.preOrdersRequestId += 1;

      const stopping = this.stopPreOrdersSocket();
      if (guid) {
        this.loadPreOrders();
        this.getCompanyGuid(guid);
      }

      await stopping;
      if (
        !this.isSocketDestroyed &&
        generation === this.socketGeneration &&
        guid === this.companyGuid &&
        guid
      ) {
        this.setupPreOrdersSocket();
      }
    },

    setupPreOrdersSocket() {
      if (this.isSocketDestroyed || !this.companyGuid || this.connection)
        return;
      const connection = createSignalRConnection("ordersHub");
      this.connection = connection;
      connection.on("OrderCreated", (id) => {
        if (connection === this.connection && !this.isSocketDestroyed) {
          this.handlePreOrderCreated(id, connection);
        }
      });
      connection.on("PreOrderAccepted", (id) => {
        if (connection === this.connection && !this.isSocketDestroyed) {
          this.handlePreOrderAccepted(id, connection);
        }
      });
      connection.onreconnected(() => {
        if (connection === this.connection && !this.isSocketDestroyed) {
          this.joinPreOrdersGroup(connection);
        }
      });
      connection.onclose(() => {
        if (connection === this.connection && !this.isSocketDestroyed) {
          this.scheduleSocketRetry(connection);
        }
      });
      this.ensureSocketConnection(connection);
    },

    async stopPreOrdersSocket() {
      if (this.socketRetryTimer) clearTimeout(this.socketRetryTimer);
      if (this.preOrdersRefreshTimer) clearTimeout(this.preOrdersRefreshTimer);
      this.socketRetryTimer = null;
      this.preOrdersRefreshTimer = null;

      const connection = this.connection;
      this.connection = null;
      if (!connection) return;

      connection.off("OrderCreated");
      connection.off("PreOrderAccepted");
      try {
        await connection.stop();
      } catch (error) {
        console.error("Error al cerrar SignalR de preórdenes:", error);
      }
    },

    scheduleSocketRetry(connection) {
      if (
        this.isSocketDestroyed ||
        !this.companyGuid ||
        connection !== this.connection
      ) {
        return;
      }

      if (this.socketRetryTimer) clearTimeout(this.socketRetryTimer);
      this.socketRetryTimer = setTimeout(() => {
        this.socketRetryTimer = null;
        this.ensureSocketConnection(connection);
      }, 3000);
    },

    async ensureSocketConnection(connection) {
      if (connection !== this.connection || this.isSocketDestroyed) return;
      if (connection.state === "Connected") {
        await this.joinPreOrdersGroup(connection);
        return;
      }
      if (
        connection.state === "Connecting" ||
        connection.state === "Reconnecting"
      ) {
        return;
      }

      try {
        await connection.start();
        if (connection !== this.connection || this.isSocketDestroyed) {
          await connection.stop();
          return;
        }
        await this.joinPreOrdersGroup(connection);
      } catch (error) {
        if (connection === this.connection) {
          console.error("Error al conectar SignalR de preórdenes:", error);
          this.scheduleSocketRetry(connection);
        }
      }
    },

    async joinPreOrdersGroup(connection) {
      const guid = this.companyGuid;
      if (
        !guid ||
        connection !== this.connection ||
        connection.state !== "Connected"
      ) {
        return;
      }

      try {
        await connection.invoke("JoinGroup", guid);
      } catch (error) {
        if (connection === this.connection) {
          console.error("Error al unirse al grupo de preórdenes:", error);
          this.scheduleSocketRetry(connection);
        }
      }
    },

    schedulePreOrdersRefresh() {
      if (this.isSocketDestroyed || !this.companyGuid) return;
      if (this.preOrdersRefreshTimer) clearTimeout(this.preOrdersRefreshTimer);
      this.preOrdersRefreshTimer = setTimeout(() => {
        this.preOrdersRefreshTimer = null;
        this.loadPreOrders({ silent: true });
      }, 300);
    },

    async handlePreOrderCreated(id, connection) {
      const preOrderId = id == null ? "" : String(id).trim();
      if (
        !/^\d+$/.test(preOrderId) ||
        connection !== this.connection ||
        this.isSocketDestroyed
      ) {
        return;
      }

      // OrderCreated se comparte con órdenes. Refrescar primero evita perder
      // la actualización si el evento llega antes de que PreOrderGet sea legible.
      this.schedulePreOrdersRefresh();

      const companyGuid = this.companyGuid;
      try {
        const response = await getPreOrder(preOrderId);
        if (
          this.isSocketDestroyed ||
          connection !== this.connection ||
          companyGuid !== this.companyGuid
        ) {
          return;
        }

        const preOrder = _.get(response, "data");
        if (
          response?.err ||
          String(preOrder?.idOrder) !== preOrderId ||
          (preOrder.guidCompany && preOrder.guidCompany !== companyGuid)
        ) {
          return;
        }

        this.alertToast(
          "success",
          this.$t(this.label("newPreOrderTitle")),
          this.$t(this.label("newPreOrderMessage"), { id: preOrderId }),
        );
      } catch (error) {
        // OrderCreated también se emite para órdenes normales; PreOrderGet no las encuentra.
      }
    },

    handlePreOrderAccepted(id, connection) {
      const preOrderId = id == null ? "" : String(id).trim();
      if (
        !/^\d+$/.test(preOrderId) ||
        connection !== this.connection ||
        this.isSocketDestroyed
      ) {
        return;
      }

      this.schedulePreOrdersRefresh();
    },

    label(field) {
      return `pages.preorders.table.${field}`;
    },

    labelHeader(field) {
      return `pages.preorders.table.tableHeader.${field}`;
    },

    onEdit(idOrder) {
      if (!this.canEdit) return;
      this.$router.push({
        name: "preorderEdit",
        params: { option: 3, guid: idOrder },
      });
    },

    canAcceptPreOrder(item) {
      return (
        this.isEmployee &&
        String(item?.nameStatus || "")
          .trim()
          .toLowerCase() === "asignada"
      );
    },

    async onAcceptPreOrder(item) {
      if (!this.canAcceptPreOrder(item)) return;

      const idOrder = Number(item.idOrder);
      if (!Number.isInteger(idOrder) || idOrder <= 0) return;

      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t(this.label("acceptPreOrderConfirmation"), {
          id: idOrder,
        }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t(this.label("acceptPreOrderConfirm")),
        cancelButtonText: this.$t("cancel"),
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.value) return;

      this.acceptingPreOrderId = idOrder;
      showLoading();
      try {
        await preOrderAccepted(idOrder);
        this.alertToast(
          "success",
          this.$t("success"),
          this.$t(this.label("acceptPreOrderSuccess"), { id: idOrder }),
        );
        await this.loadPreOrders({ silent: true });
      } catch (error) {
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error?.data?.message ||
            error?.message ||
            this.$t(this.label("acceptPreOrderError")),
        );
      } finally {
        this.acceptingPreOrderId = null;
        stopLoading();
      }
    },

    onDelete(idOrder) {
      if (!this.canDelete) return;
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          showLoading();
          preOrderDelete(idOrder)
            .then(() => {
              stopLoading();
              this.$swal({
                icon: "success",
                title: this.$t("Deleted"),
                text: this.$t("beenDeleted"),
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              this.loadPreOrders();
            })
            .catch((error) => {
              console.log("error", error.message);
              stopLoading();
              this.alertToast(
                "danger",
                this.$t("codeAlert"),
                error?.data?.message || "Error al eliminar la pre orden",
                "AlertTriangleIcon",
              );
            });
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    onWhatsApp(item) {
      const whatsappUrl = item.urlChatBoot;
      window.open(whatsappUrl, "_blank");
    },

    onSOFIA(item) {
      const sofiasappUrl = item.urlChatSofia;
      window.open(sofiasappUrl, "_blank");
    },

    colorStatus(status) {
      switch (status) {
        case "Generada":
          return "secondary";
        case "Atendida":
          return "warning";
        case "Procesada":
          return "info";
        case "Terminada":
          return "success";
        case "Cancelada":
          return "danger";
        default:
          return "primary";
      }
    },

    dateFormat(date) {
      if (!date) return "-";
      const normalizedDate =
        typeof date === "string" ? date.trim().replace(" ", "T") : date;
      const utcDate =
        typeof normalizedDate === "string" &&
        !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedDate)
          ? `${normalizedDate}Z`
          : normalizedDate;
      const fecha = new Date(utcDate);
      if (Number.isNaN(fecha.getTime())) return "-";
      const dia = fecha.getDate().toString().padStart(2, "0");
      const mes = (fecha.getMonth() + 1).toString().padStart(2, "0");
      const anio = fecha.getFullYear();
      const hour24 = fecha.getHours();
      const horas = (hour24 % 12 || 12).toString().padStart(2, "0");
      const minutos = fecha.getMinutes().toString().padStart(2, "0");
      const period = hour24 >= 12 ? "PM" : "AM";
      return `${dia}/${mes}/${anio} ${horas}:${minutos} ${period}`;
    },

    chargeParams() {
      const arrParams = [];
      arrParams.push(`guidCompany,${this.companyGuid}`);
      arrParams.push(`page,${this.currentPage}`);
      arrParams.push(`rows,${this.perPage}`);

      const roleAdmin = this.$store.getters["appConfig/role"];
      if (this.isEmployee && roleAdmin != "JEFEDEVENTAS") {
        const idEmployee = this.$store.getters["appConfig/idEmployee"];

        if (!Number.isInteger(idEmployee) || idEmployee <= 0) {
          console.error("Invalid employee identifier in session context");
          return null;
        }

        arrParams.push(`idEmployee,${idEmployee}`);
      }
      const params = getParams(arrParams);
      if (this.appliedFilters.name) {
        params.append("name", this.appliedFilters.name);
      }
      return params;
    },

    applyFilters(filters) {
      const name = typeof filters?.name === "string" ? filters.name.trim() : "";
      this.appliedFilters = name ? { name } : {};
      this.currentPage = 1;
      this.hasNextPage = true;
      this.loadPreOrders();
    },

    resetFilters() {
      this.appliedFilters = {};
      this.currentPage = 1;
      this.hasNextPage = true;
      this.loadPreOrders();
    },

    async loadPreOrders({ silent = false } = {}) {
      if (!this.companyGuid || this.isSocketDestroyed) return;
      const companyGuid = this.companyGuid;
      const requestId = ++this.preOrdersRequestId;
      const param = this.chargeParams();
      if (!param) {
        this.items = [];
        this.totalRows = 0;
        this.hasNextPage = false;
        return;
      }
      if (!silent) {
        this.loading = true;
        showLoading();
      }

      try {
        const resp = await allPreOrdersByCompany(param);
        if (
          requestId !== this.preOrdersRequestId ||
          companyGuid !== this.companyGuid ||
          this.isSocketDestroyed
        ) {
          return;
        }

        const data = _.get(resp, "data");
        if (resp.status === 500) {
          this.alertToast("danger", "Alerta", data?.message);
          this.items = [];
          this.totalRows = 0;
          return;
        }
        this.items = data?.items || [];
        this.totalRows = data?.totalRows || 0;
        this.hasNextPage = Boolean(data?.currentPage < data?.totalPages);
      } catch (error) {
        if (
          requestId !== this.preOrdersRequestId ||
          companyGuid !== this.companyGuid ||
          this.isSocketDestroyed
        ) {
          return;
        }

        const message =
          error?.data?.message ||
          error?.message ||
          "Error al cargar pre órdenes";

        if (
          message.toLowerCase().includes("no autenticado") ||
          message.toLowerCase().includes("unauthorized")
        ) {
          this.alertToast(
            "danger",
            "Alerta",
            "No autenticado. Por favor inicia sesión nuevamente.",
          );
          return;
        }

        this.alertToast("danger", "Alerta codigo", message);
      } finally {
        if (!silent) {
          this.loading = false;
          stopLoading();
        }
      }
    },

    handlePageChange(value) {
      this.currentPage = value;
      this.hasNextPage = true;
      this.loadPreOrders();
    },

    handlePerPageChange() {
      this.loadPreOrders();
    },

    alertToast(type = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon: "CoffeeIcon",
      });
    },

    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },
    async getCompanyGuid(guid) {
      showLoading();
      await getCompany(guid)
        .then((resp) => {
          console.log("resp", resp);
          stopLoading();
          const company = _.get(resp, "data");
          let phoneFormat = company.phoneNumber.replace(/\s/g, "");
          if (guid === this.companyGuid && !this.isSocketDestroyed) {
            this.whatsappPhone = phoneFormat;
          }
        })
        .catch((error) => {
          stopLoading();
          console.log("error", error);
          this.alertToast(
            "danger",
            "Error",
            error.message,
            "AlertTriangleIcon",
          );
        });
    },
  },
  beforeDestroy() {
    this.isSocketDestroyed = true;
    this.socketGeneration += 1;
    this.preOrdersRequestId += 1;
    this.stopPreOrdersSocket();
  },
};
</script>
<style scoped>
.accept-preorder-button,
.sofia-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  vertical-align: middle;
  cursor: pointer;
}

.accept-preorder-button:disabled {
  cursor: wait;
  opacity: 0.5;
}

.accept-preorder-button:focus-visible,
.sofia-action-button:focus-visible {
  outline: 2px solid #7367f0;
  outline-offset: 3px;
  border-radius: 3px;
}

.employee-assignment-list,
.employee-assignment {
  display: flex;
  flex-direction: column;
}

.employee-assignment-list {
  gap: 0.5rem;
}

.employee-assignment-name {
  font-weight: 500;
}
</style>
